<div class="flex max-w-5xl flex-col gap-4">
  <!-- Intro video -->
  @if (videoUrl) {
    <section
      id="help-block-introVideo"
      class="rounded-lg border border-gray-200 bg-white p-4"
    >
      <div
        class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
      >
        <div class="flex items-start gap-4">
          <div
            class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100"
          >
            <lucide-icon name="video" class="text-primary-600" [size]="24" />
          </div>
          <div>
            <h2 class="mb-2 text-heading-2 text-greyscale-950">
              {{ "help.onboarding.video.title" | transloco }}
            </h2>
            <p class="max-w-2xl text-paragraph-reg text-greyscale-600">
              {{ "help.onboarding.video.description" | transloco }}
            </p>
          </div>
        </div>
        @if (!videoOpen()) {
          <p-button (click)="videoOpen.set(true)">
            <lucide-icon name="play" [size]="18" />
            {{ "help.onboarding.video.cta" | transloco }}
          </p-button>
        } @else {
          <button
            (click)="videoOpen.set(false)"
            class="flex h-8 w-8 items-center justify-center rounded-full text-greyscale-500 transition-colors hover:bg-greyscale-100 hover:text-greyscale-800"
          >
            <lucide-icon name="x" [size]="18" />
          </button>
        }
      </div>
      @if (videoOpen()) {
        <video
          [src]="videoUrl"
          controls
          preload="metadata"
          class="mt-4 w-full"
        ></video>
      }
    </section>
  }

  <!-- MOOCs -->
  <section
    id="help-block-moocs"
    class="rounded-lg border border-gray-200 bg-white p-4"
  >
    <div class="mb-6 flex items-center gap-3">
      <div
        class="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100"
      >
        <lucide-icon
          name="graduation-cap"
          class="text-primary-500"
          [size]="24"
        />
      </div>
      <h2 class="text-heading-2 text-greyscale-950">
        {{ "help.onboarding.journey.blocks.moocs" | transloco }}
      </h2>
    </div>

    <div class="flex flex-col gap-3">
      @for (video of youtubeVideos; track video.titleKey; let i = $index) {
        <div
          class="flex flex-col gap-4 rounded-lg border border-greyscale-200 bg-primary-50 p-4"
        >
          <div class="flex flex-row flex-wrap items-center justify-between">
            <div class="flex items-center gap-4">
              <lucide-icon
                name="play-circle"
                class="text-primary-500"
                [size]="24"
              />

              <div>
                <h3 class="text-heading-4 text-greyscale-900">
                  {{ video.titleKey | transloco }}
                </h3>
                @if (activeLang() !== "fr") {
                  <p class="mt-1 text-sm text-greyscale-500">
                    {{ "help.onboarding.resources.videos.altInfo" | transloco }}
                    <a
                      [href]="video.altUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-primary-600 underline hover:text-primary-800"
                      >{{
                        "help.onboarding.resources.videos.altCta" | transloco
                      }}</a
                    >
                  </p>
                }
              </div>
            </div>
            @if (!youtubeOpenStates[i]()) {
              <p-button
                (click)="youtubeOpenStates[i].set(true)"
                [outlined]="true"
              >
                <lucide-icon name="play" [size]="18" />
                {{ "help.onboarding.video.cta" | transloco }}
              </p-button>
            } @else {
              <button
                (click)="youtubeOpenStates[i].set(false)"
                class="flex h-8 w-8 items-center justify-center rounded-full text-greyscale-500 transition-colors hover:bg-greyscale-200 hover:text-greyscale-800"
              >
                <lucide-icon name="x" [size]="18" />
              </button>
            }
          </div>
          @if (youtubeOpenStates[i]()) {
            <div class="aspect-video w-full overflow-hidden rounded-lg">
              <iframe
                [src]="embedUrl(video.videoIdFr)"
                class="h-full w-full"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
          }
        </div>
      }
    </div>
  </section>

  <!-- Quiz -->
  <div id="help-block-quiz">
    <app-quiz />
  </div>

  <!-- Resources: the documents to hand to the team -->
  <section
    id="help-block-resources"
    class="rounded-lg border border-gray-200 bg-white p-4"
  >
    <div class="mb-6 flex items-center gap-3">
      <div
        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100"
      >
        <lucide-icon name="layers" class="text-primary-500" [size]="24" />
      </div>
      <h2 class="text-heading-2 text-greyscale-950">
        {{ "help.onboarding.resources.title" | transloco }}
      </h2>
    </div>
    <p class="mb-6 text-paragraph-reg text-greyscale-600">
      {{ "help.onboarding.resources.description" | transloco }}
    </p>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      @for (resource of resources; track resource.i18nKey) {
        <div
          class="flex flex-col gap-3 rounded-lg border border-greyscale-200 bg-primary-50 p-4"
        >
          <div class="flex items-center gap-2">
            <lucide-icon
              [name]="resource.icon"
              class="shrink-0 text-primary-500"
              [size]="24"
            />
            <h3 class="text-heading-4 text-greyscale-900">
              {{ resource.i18nKey + ".title" | transloco }}
            </h3>
            <span class="text-xs text-greyscale-500">
              @for (fmt of resource.formats; track fmt.type; let last = $last) {
                {{ fmt.type }}{{ last ? "" : " / " }}
              }
            </span>
          </div>
          <p class="text-sm text-greyscale-600">
            {{ resource.i18nKey + ".description" | transloco }}
          </p>

          <!-- Objective / when / how: how to actually use the document, not just what it is -->
          <dl
            class="flex flex-1 flex-col gap-3 border-t border-greyscale-200 pt-3"
          >
            @for (detail of resourceDetails; track detail) {
              <div>
                <dt
                  class="text-paragraph-xs-med uppercase tracking-wide text-primary-600"
                >
                  {{ "help.onboarding.resources.labels." + detail | transloco }}
                </dt>
                <dd class="text-sm text-greyscale-700">
                  {{ resource.i18nKey + "." + detail | transloco }}
                </dd>
              </div>
            }
          </dl>

          <div class="flex gap-2">
            @for (fmt of resource.formats; track fmt.type) {
              <a
                [href]="resourceUrl(fmt.urls)"
                target="_blank"
                rel="noopener noreferrer"
                download
                class="p-button p-button-outlined w-full justify-center !text-xs"
              >
                <lucide-icon name="download" [size]="12" />
                {{ "help.onboarding.resources.download" | transloco }}
                {{ fmt.type }}
              </a>
            }
          </div>
        </div>
      }
    </div>
  </section>

  <!-- Webinars -->
  <section
    id="help-block-webinars"
    class="rounded-lg border border-gray-200 bg-white p-4"
  >
    <div class="mb-6 flex items-center gap-3">
      <div
        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100"
      >
        <lucide-icon name="video" class="text-primary-500" [size]="24" />
      </div>
      <h2 class="text-heading-2 text-greyscale-950">
        {{ "help.onboarding.webinars.title" | transloco }}
      </h2>
    </div>
    <p class="mb-6 text-paragraph-reg text-greyscale-600">
      {{ "help.onboarding.webinars.description" | transloco }}
    </p>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      @for (webinar of webinars; track webinar.titleKey) {
        <app-webinar-card [webinar]="webinar" />
      }
    </div>
  </section>
</div>
